
import axios, { AxiosError, AxiosResponse } from "axios";

type GetRequest = (path: string) => Promise<any>;

type PostRequest = (path: string, data?: any) => Promise<any>;

type PutRequest = (path: string, data?: any) => Promise<any>;

type DeleteRequest = (path: string, data?: any) => Promise<any>;

/**
 * A helper function to simplify making GET requests using axios to the ABAC API.
 * @param path The API path to call
 * @returns AxiosResponse object or throws an Error
 */
export const abacGetRequest: GetRequest = async function (path) {
  let rootUrl = import.meta.env.VITE_ABAC_API;

  const response = axios
    .get(`${rootUrl}/api${path}`)
    .catch((error: AxiosError) => {
      throw new Error(error.message);
    })
    .then((res) => res);

  return await response;
};

/**
 * A helper function to simplify making GET requests using axios to the Client Service API.
 * @param path The API path to call
 * @returns AxiosResponse object or throws an Error
 */
export const clientServiceGetRequest: GetRequest = async function (path) {
  let rootUrl = import.meta.env.VITE_CLIENTSERVICE_API;

  const response = axios
    .get(`${rootUrl}/${path}`)
    .catch((error: AxiosError) => {
      throw new Error(error.message);
    })
    .then((res) => res);

  return await response;
};

/**
 * A helper function to simplify making POST requests using axios to the ABAC API.
 * @param path The API path to call
 * @param data The POST data to send the API
 * @returns AxiosResponse object or throws an Error
 */
export const abacPostRequest: PostRequest = async function (path, data) {
  let rootUrl = import.meta.env.VITE_ABAC_API;

  const response = axios.post(`${rootUrl}/api${path}`, data).then((res) => res);
  return await response;
};

/**
 * A helper function to simplify making PUT requests using axios to the ABAC API.
 * @param path The API path to call
 * @param data The PUT data to send the API
 * @returns AxiosResponse object or throws an Error
 */
export const abacPutRequest: PutRequest = async function (path, data) {
  let rootUrl = import.meta.env.VITE_ABAC_API;

  const response = axios.put(`${rootUrl}/api${path}`, data).then((res) => res);

  return await response;
};

/**
 * A helper function to simplify making DELETE requests using axios to the ABAC API.
 * @param path The API path to call
 * @returns AxiosResponse object or throws an Error
 */
export const abacDeleteRequest: DeleteRequest = async function (path) {
  let rootUrl = import.meta.env.VITE_ABAC_API;

  const response = axios
    .delete(`${rootUrl}/api${path}`)
    .catch((error: AxiosError) => {
      throw new Error(error.message);
    })
    .then((res) => res);

  return await response;
};

// Apps
// GET
// /api/App/GetAllApps
// /api/App/GetApp/{appId}
// POST
// /api/App/CreateApp
// PUT
// /api/App/UpdateApp
export const abacAppsControllerPath = "/App/";

// Features
// GET
// /api/Feature/GetAllFeaturesForApp/{appId}
// /api/Feature/GetFeature/{featureId}
// POST
// /api/Feature/CreateFeature
// PUT
// /api/Feature/UpdateFeature
export const abacFeaturesControllerPath = "/Feature/";

// Attributes
// GET
// /api/Attribute/GetAllAttributesForApp/{appId}
// POST
// /api/Attribute/CreateAttribute
export const abacAttributesControllerPath = "/Attribute/";

// Feature Attributes
// GET
// /api/FeatureAttributes/GetAllFeatureAttributesForFeature/{featureId}
export const abacFeatureAttributesControllerPath = "/FeatureAttribute/";

// User Attributes
// GET
// /api/UserClientAttribute/GetUserClientAttributesByClient/{userId}/{clientId}
// /api/UserClientAttribute/GetAllUserClientAttributes/{userId}
// POST
// /api/UserClientAttribute/CreateUserClientAttribute
// DELETE
// /api/UserClientAttribute/DeleteUserClientAttribute/{attributeId}
export const abacUserClientAttributeControllerPath = "/UserClientAttribute/";

// Permission
// GET
// /api/Permission/GetAppFeatures/{appKey}/{userId}
export const abacPermissionControllerPath = "/Permission/";

// Users
// GET
// /api/User/GetUsersForClient/{clientId}
export const abacUserControllerPath = "/User/";

// Feature Client Attributes
// GET
// /api/FeatureClientAttribute/GetAllFeatureClientAttributes?appId={appId}&baseClientId={baseClientId}&featureId={featureId}
// /api/FeatureClientAttribute/GetFeatureClientAttribute/{featureClientAttributeId}
// POST
// /api/FeatureClientAttribute/SyncFeatureClientAttributes
// DELETE
// /api/FeatureClientAttribute/DeleteFeatureClientAttribute/{featureClientAttributeId}
export const abacFeatureClientAttributeControllerPath = "/FeatureClientAttribute/";
